/**
 * AdminSchemaGroupsTab Component
 *
 * Admin interface for managing Products (verticals) and Schema Groups.
 * Displays two product types:
 * - Platform Products: TerraMatrix-controlled vertical SaaS solutions
 * - Organization Products: Org-specific custom workspaces
 *
 * Products organize groups by industry vertical (Brewing, Agriculture, etc.)
 * Groups are containers that hold schema memberships with retention policies.
 * Only visible to admin users.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Package, Globe, Building2 } from 'lucide-react';
import {
  groupApi,
  Product,
  ProductWithGroups,
  SchemaGroup,
  ProductType,
} from '@/services/api/groupApi';
import { ProductCard } from './ProductCard';
import { AddProductDialog } from './AddProductDialog';
import { AddGroupDialog } from './AddGroupDialog';
import { EditGroupDialog } from './EditGroupDialog';
import { ManageSchemasDialog } from './ManageSchemasDialog';
import { AssignOrganizationsDialog } from './AssignOrganizationsDialog';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import { FOREST_GREEN } from '@/styles/tokens';
import * as errorUtils from '@/utils/errors';

function toProductWithGroups(product: Product | ProductWithGroups): ProductWithGroups {
  const groups = 'groups' in product && Array.isArray(product.groups) ? product.groups : [];
  return { ...product, groups };
}

export const AdminSchemaGroupsTab: React.FC = () => {
  const isAdmin = useOrganizationStore((state) => state.platformAdminEntitlement);

  // Data state
  const [products, setProducts] = useState<ProductWithGroups[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Separate platform and organization products
  const { platformProducts, orgProducts } = useMemo(() => {
    const platform: ProductWithGroups[] = [];
    const org: ProductWithGroups[] = [];

    products.forEach((product) => {
      if (product.type === 'platform') {
        platform.push(product);
      } else {
        org.push(product);
      }
    });

    return { platformProducts: platform, orgProducts: org };
  }, [products]);

  // Dialog states
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showAddGroup, setShowAddGroup] = useState(false);
  const [showEditGroup, setShowEditGroup] = useState(false);
  const [showManageSchemas, setShowManageSchemas] = useState(false);
  const [showAssignOrgs, setShowAssignOrgs] = useState(false);

  // Selected items for dialogs
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<SchemaGroup | null>(null);

  // Product type for add dialog (platform or organization)
  const [addProductType, setAddProductType] = useState<ProductType>('organization');

  // Expanded product state
  const [expandedProducts, setExpandedProducts] = useState<Set<string>>(new Set());

  // Fetch products with their groups
  const fetchProducts = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const productList = await groupApi.listProducts({ includeGroups: true, includeInactive: true });
      setProducts(productList.map(toProductWithGroups));
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to load products');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Toggle product expansion
  const toggleProductExpanded = useCallback((productId: string) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) {
        next.delete(productId);
      } else {
        next.add(productId);
      }
      return next;
    });
  }, []);

  // Handle add product with specific type
  const handleAddProduct = useCallback((type: ProductType) => {
    setAddProductType(type);
    setShowAddProduct(true);
  }, []);

  // Handle add group (opens dialog with selected product)
  const handleAddGroup = useCallback((product: Product) => {
    setSelectedProduct(product);
    setShowAddGroup(true);
  }, []);

  // Handle manage schemas (opens dialog for a group)
  const handleManageSchemas = useCallback((group: SchemaGroup) => {
    setSelectedGroup(group);
    setShowManageSchemas(true);
  }, []);

  // Handle assign organizations (opens dialog for a group)
  const handleAssignOrganizations = useCallback((group: SchemaGroup) => {
    if (!isAdmin) {
      return;
    }
    setSelectedGroup(group);
    setShowAssignOrgs(true);
  }, [isAdmin]);

  // Handle edit group
  const handleEditGroup = useCallback((group: SchemaGroup) => {
    setSelectedGroup(group);
    setShowEditGroup(true);
  }, []);

  // Handle delete group
  const handleDeleteGroup = useCallback(
    async (group: SchemaGroup) => {
      if (!window.confirm(`Are you sure you want to delete "${group.name}"? This action cannot be undone.`)) {
        return;
      }

      try {
        await groupApi.deleteGroup(group.id);
        await fetchProducts();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to delete group');
        alert(errorMessage);
      }
    },
    [fetchProducts]
  );

  // Handle edit product
  const handleEditProduct = useCallback((product: Product) => {
    // TODO: Implement edit product dialog
    console.log('Edit product:', product);
  }, []);

  // Handle delete product
  const handleDeleteProduct = useCallback(
    async (product: Product) => {
      if (!window.confirm(`Are you sure you want to delete "${product.name}"? This action cannot be undone.`)) {
        return;
      }

      try {
        await groupApi.deleteProduct(product.id);
        await fetchProducts();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to delete product');
        alert(errorMessage);
      }
    },
    [fetchProducts]
  );

  // Handle toggle product status
  const handleToggleProductStatus = useCallback(
    async (product: Product) => {
      try {
        if (product.status === 'active') {
          await groupApi.deactivateProduct(product.id);
        } else {
          await groupApi.activateProduct(product.id);
        }
        await fetchProducts();
      } catch (err: unknown) {
        const errorMessage =
          errorUtils.getErrorMessageOrFallback(err, 'Failed to update product status');
        alert(errorMessage);
      }
    },
    [fetchProducts]
  );

  // Dialog success handlers
  const handleAddProductSuccess = useCallback(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleAddGroupSuccess = useCallback(() => {
    fetchProducts();
    setSelectedProduct(null);
  }, [fetchProducts]);

  const handleManageSchemasSuccess = useCallback(() => {
    fetchProducts();
    setSelectedGroup(null);
  }, [fetchProducts]);

  const handleAssignOrgsSuccess = useCallback(() => {
    fetchProducts();
    setSelectedGroup(null);
  }, [fetchProducts]);

  const handleEditGroupSuccess = useCallback(() => {
    fetchProducts();
    setSelectedGroup(null);
  }, [fetchProducts]);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">Schema Groups</h2>
            <p className="text-sm text-muted-foreground">
              Manage Products (Platform verticals & Organization workspaces), Schema Groups, and organization assignments.
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                onClick={() => handleAddProduct('platform')}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              >
                <Globe className="h-4 w-4 mr-2" />
                + Platform Product
              </Button>
            )}
            <Button
              onClick={() => handleAddProduct('organization')}
              className="text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.DEFAULT)}
            >
              <Building2 className="h-4 w-4 mr-2" />
              + Org Product
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center h-40">
            <div className="text-muted-foreground">Loading products...</div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="p-4 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400">
            <div className="font-medium">Error loading products</div>
            <div className="text-sm mt-1">{error}</div>
            <Button
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={fetchProducts}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !error && products.length === 0 && (
          <div className="flex flex-col items-center justify-center h-40 text-center">
            <Package className="h-12 w-12 text-muted-foreground mb-4" />
            <div className="text-lg font-medium">No products yet</div>
            <div className="text-sm text-muted-foreground mb-4">
              Create a product to organize your schema groups.
            </div>
            <Button
              onClick={() => handleAddProduct('organization')}
              className="text-white"
              style={{ backgroundColor: FOREST_GREEN.DEFAULT }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.hover)}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.DEFAULT)}
            >
              + Create Your First Product
            </Button>
          </div>
        )}

        {/* Product sections */}
        {!isLoading && !error && products.length > 0 && (
          <div className="space-y-8">
            {/* Platform Products Section */}
            {platformProducts.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">Platform Products</h3>
                  <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    {platformProducts.length}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  TerraMatrix-controlled vertical SaaS solutions. Organizations can subscribe to these products.
                </p>
                <div className="space-y-4">
                  {platformProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      groups={product.groups || []}
                      isExpanded={expandedProducts.has(product.id)}
                      onToggleExpanded={() => toggleProductExpanded(product.id)}
                      onEdit={() => handleEditProduct(product)}
                      onDelete={() => handleDeleteProduct(product)}
                      onToggleStatus={() => handleToggleProductStatus(product)}
                      onAddGroup={() => handleAddGroup(product)}
                      onEditGroup={handleEditGroup}
                      onDeleteGroup={handleDeleteGroup}
                      onManageSchemas={handleManageSchemas}
                      onAssignOrganizations={handleAssignOrganizations}
                      isAdmin={isAdmin}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Organization Products Section */}
            {orgProducts.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-5 w-5 text-green-600" />
                  <h3 className="text-lg font-semibold">Organization Products</h3>
                  <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                    {orgProducts.length}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Organization-specific workspaces. Your organization has full control over these products.
                </p>
                <div className="space-y-4">
                  {orgProducts.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      groups={product.groups || []}
                      isExpanded={expandedProducts.has(product.id)}
                      onToggleExpanded={() => toggleProductExpanded(product.id)}
                      onEdit={() => handleEditProduct(product)}
                      onDelete={() => handleDeleteProduct(product)}
                      onToggleStatus={() => handleToggleProductStatus(product)}
                      onAddGroup={() => handleAddGroup(product)}
                      onEditGroup={handleEditGroup}
                      onDeleteGroup={handleDeleteGroup}
                      onManageSchemas={handleManageSchemas}
                      onAssignOrganizations={handleAssignOrganizations}
                      isAdmin={isAdmin}
                    />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddProductDialog
        open={showAddProduct}
        onOpenChange={setShowAddProduct}
        onSuccess={handleAddProductSuccess}
        productType={addProductType}
      />

      <AddGroupDialog
        open={showAddGroup}
        onOpenChange={setShowAddGroup}
        product={selectedProduct}
        onSuccess={handleAddGroupSuccess}
      />

      <EditGroupDialog
        open={showEditGroup}
        onOpenChange={setShowEditGroup}
        group={selectedGroup}
        onSuccess={handleEditGroupSuccess}
      />

      <ManageSchemasDialog
        open={showManageSchemas}
        onOpenChange={setShowManageSchemas}
        group={selectedGroup}
        onSuccess={handleManageSchemasSuccess}
      />

      <AssignOrganizationsDialog
        open={showAssignOrgs}
        onOpenChange={setShowAssignOrgs}
        group={selectedGroup}
        onSuccess={handleAssignOrgsSuccess}
      />
    </div>
  );
};

export default AdminSchemaGroupsTab;
